import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, switchMap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { DoctorsService } from '../../../../services/doctors';
import { JobApplicationService } from '../../../../services/job-application';

@Component({
  selector: 'app-applyfordoctor',
  templateUrl: './applyfordoctor.component.html',
  styleUrl: './applyfordoctor.component.scss'
})
export class ApplyfordoctorComponent implements OnInit {
  @Input() jobId: number | string | null = null;
  @Output() applicationSubmitted = new EventEmitter<any>();

  form!: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  selectedCv: File | null = null;
  selectedImage: File | null = null;
  currentStep = 0;
  readonly steps = [1, 2, 3];
  private readonly maxCvSize = 5 * 1024 * 1024;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private doctorsService: DoctorsService,
    private applicationService: JobApplicationService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.jobId = this.jobId || this.route.snapshot.paramMap.get('id');
    this.form = this.fb.group({
      name: ['', Validators.required],
      specialty: [''],
      university: [''],
      email: ['', Validators.email],
      graduation_year: [null, [Validators.min(1940), Validators.max(2100)]],
      has_master: [false],
      master_source: [''],
      has_phd: [false],
      phd_source: [''],
      fellowship: [''],
      classification_status: [''],
      ksa_experience: [false],
      residence_place: [''],
      birth_place: [''],
      mobile: ['', Validators.maxLength(30)],
      transfer_of_sponsorship: [false],
      offer: [false],
      visa: [false],
      has_dataflow: [false],
      notes: ['']
    });
  }

  submit(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.jobId) {
      this.errorMessage = this.t('jobUnavailable');
      return;
    }

    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.selectedCv) {
      this.errorMessage = this.t('cvRequired');
      return;
    }

    if (!this.form.value.mobile?.trim()) {
      this.errorMessage = this.t('phoneRequiredForCv');
      return;
    }

    this.isSubmitting = true;

    this.doctorsService
      .create(this.buildDoctorFormData())
      .pipe(
        switchMap((doctorResponse: any) => {
          const doctorId = doctorResponse?.data?.id || doctorResponse?.id;

          if (!doctorId) {
            throw new Error('Doctor profile was created without an ID.');
          }

          return this.applicationService.publicApplyToJob({
            job_id: this.jobId,
            doctor_id: doctorId,
            applicant_name: this.form.value.name.trim(),
            applicant_phone: this.form.value.mobile.trim(),
            applicant_email: this.form.value.email?.trim(),
            cv: this.selectedCv
          });
        }),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: (applicationResponse: any) => this.completeApplication(applicationResponse),
        error: (error: any) => this.showRequestError(error)
      });
  }

  onCvChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';

    if (!file) return;

    const name = file.name.toLowerCase();
    const validType = ['.pdf', '.doc', '.docx'].some(extension => name.endsWith(extension));

    if (!validType) {
      this.selectedCv = null;
      this.errorMessage = this.t('invalidCv');
      return;
    }

    if (file.size > this.maxCvSize) {
      this.selectedCv = null;
      this.errorMessage = this.t('cvTooLarge');
      return;
    }

    this.errorMessage = '';
    this.selectedCv = file;
  }

  removeCv(): void {
    this.selectedCv = null;
  }

  onImageChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] || null;
    this.selectedImage = file?.type.startsWith('image/') ? file : null;
  }

  goBack(): void {
    this.router.navigate(['/jobs']);
  }

  nextStep(): void {
    if (this.currentStep === 0 && (this.form.get('name')?.invalid || this.form.get('email')?.invalid)) {
      this.form.get('name')?.markAsTouched();
      this.form.get('email')?.markAsTouched();
      return;
    }
    this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  previousStep(): void { this.currentStep = Math.max(this.currentStep - 1, 0); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  getStepLabel(index: number): string {
    const labels = [
      this.isAr ? 'البيانات' : 'Data',
      this.isAr ? 'الصورة الشخصية' : 'Profile photo',
      this.isAr ? 'رفع السيرة الذاتية' : 'CV upload'
    ];
    return labels[index];
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return Boolean(control?.invalid && (control.dirty || control.touched));
  }

  get isAr(): boolean {
    return this.transloco.getActiveLang().startsWith('ar');
  }

  private buildDoctorFormData(): FormData {
    const formData = new FormData();
    const values = this.form.getRawValue();

    Object.entries(values).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        formData.append(key, value ? '1' : '0');
      } else if (value !== null && value !== undefined && value !== '') {
        formData.append(key, String(value));
      }
    });

    if (this.selectedImage) formData.append('doctor_image', this.selectedImage);

    formData.append('add_by', localStorage.getItem('username') || 'public-application');
    formData.append('is_doctor', '1');

    return formData;
  }

  private completeApplication(applicationResponse: any): void {
    this.successMessage = this.t('publicApplySuccess');
    this.applicationSubmitted.emit(applicationResponse);
    setTimeout(() => this.router.navigate(['/jobs']), 1200);
  }

  private showRequestError(error: any): void {
    this.errorMessage = error?.error?.message || error?.message || this.t('requestFailed');
  }

  private t(key: string): string {
    return this.transloco.translate(`atw.doctorApply.${key}`);
  }
}
